import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { CreditCard } from '@liveauctioneers/caterwaul-components/lib/types/CreditCard';
import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { useMemo } from 'react';

import { PaymentMethods, PaymentProviders } from '@/enums';
import { PaymentMethodAchSelection } from '@/pages/Checkout/components/Steps/Payment/components/PaymentMethodAchSelection/PaymentMethodAchSelection';
import { PaymentMethodCard } from '@/pages/Checkout/components/Steps/Payment/components/PaymentMethodCard/PaymentMethodCard';
import { PaymentMethodCreditCardsSelection } from '@/pages/Checkout/components/Steps/Payment/components/PaymentMethodCreditCardsSelection/PaymentMethodCreditCardsSelection';
import { PaymentMethodZipPlan } from '@/pages/Checkout/components/Steps/Payment/components/PaymentMethodZipPlan/PaymentMethodZipPlan';
import { useAppSelector } from '@/redux/hooks';
import { getCreditCardError } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import {
    getCatalogHighRiskPayments,
    getCatalogPaymentProvider,
    getCatalogRequiredACH,
    getCatalogSellerName,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';

type Props = {
    active: boolean;
    amountToPayZip: number;
    bidderPaysProcessingFee: boolean;
    catalogId: number;
    currency?: string;
    hadZipError: boolean;
    onBankAccountSelectionChange: (bankAccount: BankAccount) => void;
    onCardSelectionChange: (creditCard: CreditCard) => void;
    onPaymentMethodChange: (paymentMethod: PaymentMethods) => void;
    paymentMethod: PaymentMethods;
    readOnly: boolean;
    requireACHForCheckout: boolean;
    selectedBankAccount: BankAccount;
    selectedCreditCard: CreditCard;
    showAchPaymentOption: boolean;
    showCardPaymentOption: boolean;
    showZipOption: boolean;
};

export const Payment = ({
    active,
    amountToPayZip,
    bidderPaysProcessingFee,
    catalogId,
    currency,
    hadZipError,
    onBankAccountSelectionChange,
    onCardSelectionChange,
    onPaymentMethodChange,
    paymentMethod,
    readOnly,
    requireACHForCheckout,
    selectedBankAccount,
    selectedCreditCard,
    showAchPaymentOption,
    showCardPaymentOption,
    showZipOption,
}: Props) => {
    const { formatMessage, formatNumber } = useIntl();

    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const catalogHighRiskPayments = useAppSelector((state) => getCatalogHighRiskPayments(state, catalogId));
    const laPaymentCardError = useAppSelector(getCreditCardError);
    const requiredAch = useAppSelector((state) => getCatalogRequiredACH(state, catalogId));
    const houseName = useAppSelector((state) => getCatalogSellerName(state, catalogId));

    const availablePaymentMethods = useMemo(() => {
        const methods: PaymentMethods[] = [];
        if (showCardPaymentOption) {
            methods.push(PaymentMethods.CreditCard);
        }
        if (showAchPaymentOption) {
            methods.push(PaymentMethods.Ach);
        }
        if (showZipOption) {
            methods.push(PaymentMethods.Zip);
        }
        return methods;
    }, [showAchPaymentOption, showCardPaymentOption, showZipOption]);

    let infoMessage;
    if (
        catalogHighRiskPayments &&
        !(
            selectedCreditCard &&
            (paymentProvider === PaymentProviders.Paysafe || paymentProvider === PaymentProviders.Payrix)
        )
    ) {
        infoMessage = <FormattedMessage id="checkout.payment.highRisk.editRegulated" />;
    }
    if (requireACHForCheckout) {
        // When requiredACH is 1 cent, all invoices must use ach and we want a slightly different message
        infoMessage = formatMessage(
            {
                id: requiredAch === 1 ? 'checkout.payment.requiredAchAll' : 'checkout.payment.requiredAch',
            },
            {
                amount: formatNumber(requiredAch / 100, {
                    currency,
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                    style: 'currency',
                }),
                house: houseName,
            }
        );
    }

    if (laPaymentCardError) {
        return (
            <BodyPrimary color="critical">
                <FormattedMessage
                    defaultMessage={laPaymentCardError}
                    id={laPaymentCardError}
                />
            </BodyPrimary>
        );
    }
    return (
        <section
            className="w-full"
            data-testid="payment-step"
        >
            {infoMessage && (
                <div className="flex pb-md">
                    <div className="mr-xs">
                        <Info
                            className="inline-flex"
                            color="primary"
                            size="sm"
                            style="fill"
                        />
                    </div>
                    <BodySecondary>{infoMessage}</BodySecondary>
                </div>
            )}
            {active && hadZipError && (
                <div className="flex flex-col pb-md">
                    <BodySecondary color="critical">
                        <FormattedMessage id="checkout.payment.zipError" />
                    </BodySecondary>
                    <Link to="https://help.liveauctioneers.com/">
                        <FormattedMessage id="checkout.general.learnMore" />
                    </Link>
                </div>
            )}
            <div className="w-full">
                {active && !readOnly && (
                    <div className="flex gap-sm">
                        {availablePaymentMethods.map((currentCardPaymentMethod) => (
                            <PaymentMethodCard
                                amountToPayZip={amountToPayZip}
                                bidderPaysProcessingFee={bidderPaysProcessingFee}
                                catalogId={catalogId}
                                currency={currency}
                                isSelected={currentCardPaymentMethod === paymentMethod}
                                key={`payment-method-card-${currentCardPaymentMethod}`}
                                onSelect={onPaymentMethodChange}
                                paymentMethod={currentCardPaymentMethod}
                                requireACHForCheckout={requireACHForCheckout}
                            />
                        ))}
                    </div>
                )}
                <div>
                    {showCardPaymentOption && (
                        <PaymentMethodCreditCardsSelection
                            active={active}
                            catalogId={catalogId}
                            isSelected={paymentMethod === PaymentMethods.CreditCard}
                            onCardSelectionChange={onCardSelectionChange}
                            selectedCreditCard={selectedCreditCard}
                        />
                    )}
                    {showAchPaymentOption && (
                        <PaymentMethodAchSelection
                            active={active}
                            isSelected={paymentMethod === PaymentMethods.Ach}
                            onBankAccountSelectionChange={onBankAccountSelectionChange}
                            selectedBankAccount={selectedBankAccount}
                        />
                    )}
                    {showZipOption && (
                        <PaymentMethodZipPlan
                            active={active}
                            amountToPayZip={amountToPayZip}
                            isSelected={paymentMethod === PaymentMethods.Zip}
                            currency={currency}
                        />
                    )}
                </div>
            </div>
        </section>
    );
};
